import type { SharedValue } from "@-/com.npmjs.react-native-reanimated";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { AppLayoutContext } from "@-/dev.improve.haniverse.routes.nav.app._context";
import { useMorphemeSelectionOverlaySlide } from "#use-morpheme-selection-overlay-slide";

export type MorphemeSelectionOverlayContentGate = Tagged<
  { hiddenFraction: SharedValue<number> },
  "MorphemeSelectionOverlayContentGate"
>;

/**
 * Whether the selection bar is on screen: from the moment selection starts until its slide-out
 * finishes. `null` is the designed hidden state (not selecting, and the exit animation is done).
 * The gate carries the slide's shared value (stable for the overlay's lifetime), so it changes only
 * on mount/unmount transitions; the bar derives its `translateY` from it.
 */
export function useMorphemeSelectionOverlayContentGate(): MorphemeSelectionOverlayContentGate | null {
  const [isSelecting] = useContextSelectors(AppLayoutContext, (c) => c.isSelecting);
  // Slide via a manual transform, never a layout animation — see the hook's docblock for why
  // (`entering={SlideInDown}` is what caused the intermittent gap above this bar).
  const { hiddenFraction, isMounted } = useMorphemeSelectionOverlaySlide({ isSelecting });
  if (!isMounted) {
    return null;
  }
  return createGate<MorphemeSelectionOverlayContentGate>({ hiddenFraction });
}
